/**
 * Frontend Image Optimizer
 * Automatycznie kompresuje obrazy przed wysłaniem do API
 */

export class ImageOptimizer {
  constructor(options = {}) {
    this.maxSizeMB = options.maxSizeMB || 5; // Maksymalny rozmiar przed kompresją
    this.targetSizeMB = options.targetSizeMB || 2; // Docelowy rozmiar po kompresji
    this.maxWidth = options.maxWidth || 800;
    this.maxHeight = options.maxHeight || 600;
    this.quality = options.quality || 0.8;
    this.formats = options.formats || ['image/jpeg', 'image/png', 'image/webp'];
  }

  /**
   * Optymalizuje obraz do określonego rozmiaru
   */
  async optimizeImage(file, targetSizeMB = this.targetSizeMB) {
    try {
      // Sprawdź czy plik jest poprawny
      if (!file || file.size === 0) {
        console.error('Nieprawidłowy plik do optymalizacji');
        return file;
      }

      console.log(`🔧 Optymalizuję obraz: ${this.getFileSizeInMB(file)}MB -> ${targetSizeMB}MB`);
      
      let currentQuality = this.quality;
      let optimizedFile = file;
      let attempts = 0;
      const maxAttempts = 5;

      while (attempts < maxAttempts) {
        attempts++;
        
        try {
          // Kompresuj z aktualną jakością
          optimizedFile = await this.compressImage(file, currentQuality);
          const optimizedSize = parseFloat(this.getFileSizeInMB(optimizedFile));
          
          console.log(`📊 Próba ${attempts}: ${optimizedSize}MB (jakość: ${currentQuality})`);
          
          // Sprawdź czy osiągnęliśmy docelowy rozmiar
          if (optimizedSize <= targetSizeMB) {
            console.log(`✅ Osiągnięto docelowy rozmiar: ${optimizedSize}MB`);
            return optimizedFile;
          }
          
          // Zmniejsz jakość dla następnej próby
          currentQuality = Math.max(0.1, currentQuality - 0.1);
        } catch (compressError) {
          console.error(`Błąd podczas kompresji (próba ${attempts}):`, compressError);
          break;
        }
      }
      
      console.log(`⚠️ Nie udało się osiągnąć docelowego rozmiaru, używam najlepszego wyniku`);
      return optimizedFile;
    } catch (error) {
      console.error('Błąd podczas optymalizacji obrazu:', error);
      return file; // Zwróć oryginalny plik w przypadku błędu
    }
  }

  /**
   * Kompresuje obraz z określoną jakością
   */
  async compressImage(file, quality = this.quality) {
    return new Promise((resolve, reject) => {
      try {
        // Sprawdź czy plik jest poprawny
        if (!file || file.size === 0) {
          reject(new Error('Nieprawidłowy plik do kompresji'));
          return;
        }

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
          try {
            // Oblicz nowe wymiary zachowując proporcje
            let { width, height } = img;
            
            if (width > this.maxWidth) {
              height = (height * this.maxWidth) / width;
              width = this.maxWidth;
            }
            
            if (height > this.maxHeight) {
              width = (width * this.maxHeight) / height;
              height = this.maxHeight;
            }

            // Ustaw wymiary canvas
            canvas.width = width;
            canvas.height = height;

            // Narysuj i skompresuj obraz
            ctx.drawImage(img, 0, 0, width, height);
            
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  // Utwórz nowy plik ze skompresowanymi danymi
                  const compressedFile = new File([blob], file.name, {
                    type: file.type,
                    lastModified: Date.now(),
                  });
                  resolve(compressedFile);
                } else {
                  reject(new Error('Nie udało się skompresować obrazu'));
                }
              },
              file.type,
              quality
            );
          } catch (error) {
            reject(new Error(`Błąd podczas przetwarzania obrazu: ${error.message}`));
          }
        };

        img.onerror = () => {
          reject(new Error('Nie udało się załadować obrazu'));
        };

        img.src = URL.createObjectURL(file);
      } catch (error) {
        reject(new Error(`Błąd podczas kompresji: ${error.message}`));
      }
    });
  }

  /**
   * Sprawdza czy obraz wymaga optymalizacji
   */
  shouldOptimize(file) {
    try {
      // Sprawdź czy plik jest poprawny
      if (!file || file.size === 0) {
        return false;
      }
      
      const fileSizeMB = parseFloat(this.getFileSizeInMB(file));
      const shouldOptimize = fileSizeMB > this.maxSizeMB;
      
      console.log(`📊 Rozmiar pliku: ${fileSizeMB}MB, wymaga optymalizacji: ${shouldOptimize}`);
      
      return shouldOptimize;
    } catch (error) {
      console.error('Błąd podczas sprawdzania czy plik wymaga optymalizacji:', error);
      return false;
    }
  }

  /**
   * Konwertuje plik na base64 z optymalizacją
   */
  async fileToOptimizedBase64(file) {
    if (this.shouldOptimize(file)) {
      console.log(`🔧 Obraz wymaga optymalizacji (${this.getFileSizeInMB(file)}MB)`);
      const optimizedFile = await this.optimizeImage(file);
      return this.fileToBase64(optimizedFile);
    } else {
      console.log(`✅ Obraz nie wymaga optymalizacji (${this.getFileSizeInMB(file)}MB)`);
      return this.fileToBase64(file);
    }
  }

  /**
   * Konwertuje plik na base64
   */
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Konwertuje base64 na byte array
   */
  base64ToByteArray(base64) {
    return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  }

  /**
   * Pobiera rozmiar pliku w MB
   */
  getFileSizeInMB(file) {
    try {
      if (!file || typeof file.size !== 'number') {
        return '0.00';
      }
      return (file.size / (1024 * 1024)).toFixed(2);
    } catch (error) {
      console.error('Błąd podczas obliczania rozmiaru pliku:', error);
      return '0.00';
    }
  }

  /**
   * Pobiera rozmiar base64 w MB
   */
  getBase64SizeInMB(base64) {
    return ((base64.length * 0.75) / (1024 * 1024)).toFixed(2);
  }

  /**
   * Konwertuje ArrayBuffer na base64
   */
  arrayBufferToBase64(buffer) {
    return new Promise((resolve, reject) => {
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      const reader = new FileReader();
      reader.onloadend = () => {
        // reader.result ma postać "data:<mime>;base64,AAAA..."
        const base64Data = reader.result.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Optymalizuje obraz z URL i konwertuje na base64
   */
  async optimizeImageFromUrl(url, targetSizeMB = this.targetSizeMB) {
    try {
      console.log(`🌐 Pobieram obraz z URL: ${url}`);
      
      // Dodaj timeout dla fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 sekund timeout
      
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const arrayBuffer = await res.arrayBuffer();

      // Tu miałoby być Twoje "optymalizowanie" – niech zwróci ArrayBuffer
      const optimizedBuffer = await this.optimizeArrayBuffer(arrayBuffer, targetSizeMB);

      // I dopiero teraz do Base64:
      const base64 = await this.arrayBufferToBase64(optimizedBuffer);
      
      console.log(`✅ Zoptymalizowany obraz z URL`);
      
      return {
        data: Array.from(new Uint8Array(optimizedBuffer)),
        contentType: 'image/jpeg',
        originalSize: (arrayBuffer.byteLength / (1024 * 1024)).toFixed(2),
        optimizedSize: this.getBase64SizeInMB(base64)
      };
    } catch (error) {
      console.error('Błąd podczas pobierania i optymalizacji obrazu:', error);
      return null;
    }
  }

  /**
   * Optymalizuje ArrayBuffer obrazu
   */
  async optimizeArrayBuffer(arrayBuffer, targetSizeMB = this.targetSizeMB) {
    try {
      // Sprawdź czy arrayBuffer jest poprawny
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        console.error('Nieprawidłowy ArrayBuffer');
        return arrayBuffer;
      }

      // Konwertuj ArrayBuffer na Blob
      const blob = new Blob([arrayBuffer], { type: 'image/jpeg' });
      const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
      
      console.log(`📊 Pobrany rozmiar: ${this.getFileSizeInMB(file)}MB`);
      
      // Zawsze optymalizuj obraz dla aktualizacji zdjęć produktów
      console.log('🔧 Rozpoczynam optymalizację obrazu...');
      const optimizedFile = await this.optimizeImage(file, targetSizeMB);
      const optimizedArrayBuffer = await optimizedFile.arrayBuffer();
      
      console.log(`✅ Zoptymalizowany rozmiar: ${(optimizedArrayBuffer.byteLength / (1024 * 1024)).toFixed(2)}MB`);
      
      return optimizedArrayBuffer;
    } catch (error) {
      console.error('Błąd podczas optymalizacji ArrayBuffer:', error);
      return arrayBuffer; // Zwróć oryginalny buffer w przypadku błędu
    }
  }
}

// Domyślna instancja
export const imageOptimizer = new ImageOptimizer({
  maxSizeMB: 0.5,    // Kompresuj jeśli > 0.5MB (zmniejszone z 1MB)
  targetSizeMB: 0.2, // Docelowy rozmiar 0.2MB (zmniejszone z 0.5MB)
  maxWidth: 400,     // Zmniejszone z 600
  maxHeight: 300,    // Zmniejszone z 450
  quality: 0.6       // Zmniejszone z 0.7
}); 